// src/Pages/SchemeReport.jsx
import { useState, useEffect } from "react";
import { Button, Table, Spin, Alert, message, Tag, Select, Row, Col } from "antd";
import { FileExcelOutlined, ReloadOutlined } from "@ant-design/icons";
import { getSchemeReport, getDistrict, getBlockByDistrict, getVillageByBlock } from "../api/User";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FilePdfOutlined } from "@ant-design/icons";

const SchemeReport = ({ dateFilter }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [districtCode, setDistrictCode] = useState(null);
  const [blockCode, setBlockCode] = useState(null);
  const [gpCode, setGpCode] = useState(null);

  // District, Block, and GP dropdown data
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [gramPanchayats, setGramPanchayats] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [loadingGPs, setLoadingGPs] = useState(false);
  const [roleId, setRoleId] = useState(null);

  const [overallTotals, setOverallTotals] = useState({
    total_families: 0,
    total_members: 0,
    total_verified: 0,
    total_not_verified: 0,
    total_pending: 0,
  });




  const columns = [
    {
      title: "SR.NO.",
      dataIndex: "sr_no",
      key: "sr_no",
      width: 50,
      fixed: "left",
      align: "center",
    },
    {
      title: "District",
      dataIndex: "district_name",
      key: "district_name",
      width: 100,
      align: "center",
      sorter: (a, b) => a.district_name?.localeCompare(b.district_name),
      ellipsis: true,
    },
    {
      title: "Block",
      dataIndex: "block_name",
      key: "block_name",
      width: 100,
      align: "center",
      sorter: (a, b) => a.block_name?.localeCompare(b.block_name),
      ellipsis: true,
    },
    {
      title: "Gram Panchayat",
      dataIndex: "gp_name",
      key: "gp_name",
      width: 120,
      align: "center",
      sorter: (a, b) => a.gp_name?.localeCompare(b.gp_name),
      ellipsis: true,
    },
    {
      title: "Village Name",
      dataIndex: "village_names",
      key: "village_names",
      width: 150,
      align: "center",
      ellipsis: true,
    },
    {
      title: "Total No. of Families",
      dataIndex: "total_families",
      key: "total_families",
      width: 90,
      align: "center",
    },
    {
      title: "Total No. of Members",
      dataIndex: "total_members",
      key: "total_members",
      width: 90,
      align: "center",
    },
    {
      title: "Total No. of Income Verified (Marked: Yes)",
      dataIndex: "total_verified",
      key: "total_verified",
      width: 120,
      align: "center",
      render: (val) => <Tag color="green">{val || 0}</Tag>,
    },
    {
      title: "Total No. of Income Not Verified (Marked: No)",
      dataIndex: "total_not_verified",
      key: "total_not_verified",
      width: 120,
      align: "center",
      render: (val) => <Tag color="red">{val || 0}</Tag>,
    },
    {
      title: "Total No. of Income Pending",
      dataIndex: "total_pending",
      key: "total_pending",
      width: 100,
      align: "center",
      render: (val) => <Tag color="orange">{val || 0}</Tag>,
    },
  ];

  // Fetch districts on component mount
  useEffect(() => {
    const role = sessionStorage.getItem("role_id");
    setRoleId(role);
    fetchDistricts();
  }, []);

  // Fetch blocks when district changes
  useEffect(() => {
    if (districtCode) {
      fetchBlocks(districtCode);
    } else {
      setBlocks([]);
      setGramPanchayats([]);
    }
  }, [districtCode]);

  // Fetch gram panchayats when block changes
  useEffect(() => {
    if (blockCode && districtCode) {
      fetchGramPanchayats(districtCode, blockCode);
    } else {
      setGramPanchayats([]);
    }
  }, [blockCode]);

  // Auto fetch when filter changes
  useEffect(() => {
    fetchReport(currentPage, pageSize);
  }, [currentPage, pageSize, districtCode, blockCode, gpCode, dateFilter]);

  const fetchDistricts = async () => {
    try {
      setLoadingDistricts(true);
      const token = sessionStorage.getItem("token");
      const userId = sessionStorage.getItem("user_id");
      const role = sessionStorage.getItem("role_id");

      const result = await getDistrict(token, userId, role);

      if (result.status === "success" && result.data) {
        setDistricts(result.data);

        // Auto-select district for BDPO (role 7) and Sarpanch (role 3)
        if ((role === "7" || role === "3") && result.data.length === 1 || result.data.length === 5 || result.data.length === 6) {
          setDistrictCode(result.data[0].district_code);
        }
      }
    } catch (err) {
      console.error("Error fetching districts:", err);
      message.error("Failed to load districts");
    } finally {
      setLoadingDistricts(false);
    }
  };

  const fetchBlocks = async (selectedDistrictCode) => {
    try {
      setLoadingBlocks(true);
      const token = sessionStorage.getItem("token");
      const userId = sessionStorage.getItem("user_id");
      const role = sessionStorage.getItem("role_id");

      const result = await getBlockByDistrict(
        token,
        selectedDistrictCode,
        userId,
        role
      );

      if (result.status === "success" && result.data) {
        setBlocks(result.data);

        // Auto-select block for BDPO (role 7)
        if (role === "7" && (result.data.length === 1 || result.data.length === 5 || result.data.length === 6)) {
          setBlockCode(result.data[0].block_code);
        }
      }
    } catch (err) {
      console.error("Error fetching blocks:", err);
      message.error("Failed to load blocks");
    } finally {
      setLoadingBlocks(false);
    }
  };

  const fetchGramPanchayats = async (selectedDistrictCode, selectedBlockCode) => {
    try {
      setLoadingGPs(true);
      const token = sessionStorage.getItem("token");
      const userId = sessionStorage.getItem("user_id");
      const role = sessionStorage.getItem("role_id");

      const result = await getVillageByBlock(
        token,
        selectedDistrictCode,
        selectedBlockCode,
        userId,
        role
      );

      if (result.status === "success" && result.data) {
        setGramPanchayats(result.data);

        // Auto-select GP for Sarpanch (role 3)
        if (role === "3" && (result.data.length === 1 || result.data.length === 5 || result.data.length === 6)) {
          setGpCode(result.data[0].gram_panchayat_code);
        }
      }
    } catch (err) {
      console.error("Error fetching gram panchayats:", err);
      message.error("Failed to load gram panchayats");
    } finally {
      setLoadingGPs(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      if (!data.length) {
        message.warning("No data to export");
        return;
      }

      setLoading(true); // Show loading indicator while fetching data

      const token = sessionStorage.getItem("token");
      const userId = sessionStorage.getItem("user_id");
      const role = sessionStorage.getItem("role_id");

      let page = 1;
      const limit = 500;
      let hasNext = true;
      let allRows = [];

      // Grand totals calculation for PDF
      let pdfTotals = {
        total_families: 0,
        total_members: 0,
        total_verified: 0,
        total_not_verified: 0,
        total_pending: 0,
      };

      // Fetch all pages
      while (hasNext) {
        const payload = {
          page,
          limit,
          user_id: userId,
          role_id: role,
          district_code: districtCode || undefined,
          block_code: blockCode || undefined,
          gram_panchayat_code: gpCode || undefined,
          period: dateFilter || undefined,
        };

        // Remove undefined values
        Object.keys(payload).forEach(
          (key) => payload[key] === undefined && delete payload[key]
        );

        const result = await getSchemeReport(token, payload);

        if (!result.success) {
          message.error("Failed to fetch full data for PDF");
          setLoading(false);
          return;
        }

        const rows = result.data || [];
        allRows = [...allRows, ...rows];

        // Calculate totals
        rows.forEach((row) => {
          pdfTotals.total_families += Number(row.total_no_of_families || 0);
          pdfTotals.total_members += Number(row.total_no_of_members || 0);
          pdfTotals.total_verified += Number(row.total_no_of_income_scheme_verified || 0);
          pdfTotals.total_not_verified += Number(row.total_no_of_income_scheme_not_verified || 0);
          pdfTotals.total_pending += Number(row.total_no_of_income_scheme_pending_verified || 0);
        });

        hasNext = result.pagination?.has_next;
        page++;
      }

      if (!allRows.length) {
        message.warning("No data found to export");
        setLoading(false);
        return;
      }

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = doc.internal.pageSize.getWidth();

      // Title
      doc.setFontSize(14);
      doc.text("Income Verification Report", pageWidth / 2, 12, {
        align: "center",
      });

      // Date
      doc.setFontSize(9);
      doc.text(
        `Generated on: ${new Date().toLocaleString()}`,
        pageWidth / 2,
        18,
        { align: "center" }
      );

      // Table Header
      const headers = columns.map((col) => col.title);

      // Table Body - Map allRows instead of current page data
      const body = allRows.map((row, index) =>
        columns.map((col) => {
          if (col.dataIndex === "sr_no") return index + 1;
          // Map API response keys to column keys if they differ, or use existing logic if row structure matches
          // The API returns different keys than the 'data' state (which is formatted). 
          // We need to map raw API response keys to the column dataIndexes used in 'columns' definition.
          // However, let's format the row first to match 'data' state structure for consistency.

          // Or direct mapping:
          if (col.dataIndex === "district_name") return row.district;
          if (col.dataIndex === "block_name") return row.block;
          if (col.dataIndex === "gp_name") return row.gram_panchayat;
          if (col.dataIndex === "village_names") return row.village_name;
          if (col.dataIndex === "total_families") return row.total_no_of_families;
          if (col.dataIndex === "total_members") return row.total_no_of_members;
          if (col.dataIndex === "total_verified") return row.total_no_of_income_scheme_verified;
          if (col.dataIndex === "total_not_verified") return row.total_no_of_income_scheme_not_verified;
          if (col.dataIndex === "total_pending") return row.total_no_of_income_scheme_pending_verified;

          return row[col.dataIndex] ?? "";
        })
      );

      body.push([
        "GRAND TOTAL",  // SR.NO column
        "",             // District
        "",             // Block
        "",             // GP
        "",             // Village
        pdfTotals.total_families.toLocaleString(),
        pdfTotals.total_members.toLocaleString(),
        pdfTotals.total_verified.toLocaleString(),
        pdfTotals.total_not_verified.toLocaleString(),
        pdfTotals.total_pending.toLocaleString(),
      ]);

      autoTable(doc, {
        startY: 24,
        head: [headers],
        body,
        theme: "grid",
        styles: {
          fontSize: 7,
          cellPadding: 2,
          halign: "center",
          valign: "middle",
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [22, 119, 255],
          textColor: 255,
          fontStyle: "bold",
        },
        didParseCell: function (data) {
          if (data.row.index === body.length - 1) {
            data.cell.styles.fillColor = [230, 247, 255];
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.textColor = [0, 0, 0];
          }
        },
        margin: { left: 5, right: 5 },
        tableWidth: "auto",
      });

      doc.save(`Income_Verification_Report_${Date.now()}.pdf`);
      message.success("PDF exported successfully");
    } catch (error) {
      console.error("PDF Export Error:", error);
      message.error("Failed to export PDF");
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async (page, limit) => {
    try {
      setLoading(true);
      setError(null);

      const token = sessionStorage.getItem("token");
      const userId = sessionStorage.getItem("user_id");
      const role = sessionStorage.getItem("role_id");

      const payload = {
        page,
        limit,
        user_id: userId,
        role_id: role,
        district_code: districtCode || undefined,
        block_code: blockCode || undefined,
        gram_panchayat_code: gpCode || undefined,
        period: dateFilter || undefined,
      };

      // Remove undefined values
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      console.log("FILTER PAYLOAD:", payload);

      const result = await getSchemeReport(token, payload);

      if (!result.success) {
        setError(result.message || "Unable to load data");
        return;
      }

      const formatted = result.data.map((row, index) => ({
        key: index + 1,
        sr_no: (page - 1) * limit + index + 1,
        district_name: row.district,
        block_name: row.block,
        gp_name: row.gram_panchayat,
        village_names: row.village_name,
        total_families: row.total_no_of_families,
        total_members: row.total_no_of_members,
        total_verified: row.total_no_of_income_scheme_verified,
        total_not_verified: row.total_no_of_income_scheme_not_verified,
        total_pending: row.total_no_of_income_scheme_pending_verified,
      }));

      setData(formatted);
      setTotalRecords(result.pagination?.total || formatted.length);
    } catch (err) {
      console.error("Fetch Report Error:", err);
      setError("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };
  const fetchAllPagesTotals = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const userId = sessionStorage.getItem("user_id");
      const role = sessionStorage.getItem("role_id");

      let page = 1;
      const limit = 500; // faster batch
      let hasNext = true;

      let totals = {
        total_families: 0,
        total_members: 0,
        total_verified: 0,
        total_not_verified: 0,
        total_pending: 0,
      };

      while (hasNext) {
        const payload = {
          page,
          limit,
          user_id: userId,
          role_id: role,
          district_code: districtCode || undefined,
          block_code: blockCode || undefined,
          gram_panchayat_code: gpCode || undefined,
        };

        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

        const result = await getSchemeReport(token, payload);

        (result.data || []).forEach((row) => {
          totals.total_families += Number(row.total_no_of_families || 0);
          totals.total_members += Number(row.total_no_of_members || 0);
          totals.total_verified += Number(row.total_no_of_income_scheme_verified || 0);
          totals.total_not_verified += Number(row.total_no_of_income_scheme_not_verified || 0);
          totals.total_pending += Number(row.total_no_of_income_scheme_pending_verified || 0);
        });

        hasNext = result.pagination?.has_next;
        page++;
      }

      setOverallTotals(totals);
    } catch (err) {
      console.error("Grand total fetch failed:", err);
    }
  };


  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    fetchReport(pagination.current, pagination.pageSize);
  };

  const handleExportExcel = async () => {
    try {
      if (!data.length) {
        message.warning("No data to export");
        return;
      }
      setLoading(true);

      const token = sessionStorage.getItem("token");
      const userId = sessionStorage.getItem("user_id");
      const role = sessionStorage.getItem("role_id");

      let page = 1;
      const limit = 500;
      let hasNext = true;

      let allRows = [];

      // Grand totals
      let totals = {
        families: 0,
        members: 0,
        verified: 0,
        not_verified: 0,
        pending: 0,
      };

      while (hasNext) {
        const payload = {
          page,
          limit,
          user_id: userId,
          role_id: role,
          district_code: districtCode || undefined,
          block_code: blockCode || undefined,
          gram_panchayat_code: gpCode || undefined,
        };

        Object.keys(payload).forEach(
          (key) => payload[key] === undefined && delete payload[key]
        );

        const result = await getSchemeReport(token, payload);

        if (!result.success) {
          message.error("Failed to fetch full data");
          return; // finally block will run
        }

        const rows = result.data || [];
        allRows = [...allRows, ...rows];

        // 🔥 Calculate totals
        rows.forEach((row) => {
          totals.families += Number(row.total_no_of_families || 0);
          totals.members += Number(row.total_no_of_members || 0);
          totals.verified += Number(row.total_no_of_income_scheme_verified || 0);
          totals.not_verified += Number(row.total_no_of_income_scheme_not_verified || 0);
          totals.pending += Number(row.total_no_of_income_scheme_pending_verified || 0);
        });

        hasNext = result.pagination?.has_next;
        page++;
      }

      if (!allRows.length) {
        message.warning("No data to export");
        return; // finally block will run
      }

      const csv = [
        columns.map((c) => `"${c.title}"`).join(","),

        ...allRows.map((row, index) =>
          [
            index + 1,
            row.district,
            row.block,
            row.gram_panchayat,
            row.village_name,
            row.total_no_of_families,
            row.total_no_of_members,
            row.total_no_of_income_scheme_verified,
            row.total_no_of_income_scheme_not_verified,
            row.total_no_of_income_scheme_pending_verified,
          ]
            .map((val) => `"${val ?? ""}"`)
            .join(",")
        ),

        // 🔥 Add Grand Total Row
        [
          "GRAND TOTAL",
          "",
          "",
          "",
          "",
          totals.families,
          totals.members,
          totals.verified,
          totals.not_verified,
          totals.pending,
        ]
          .map((val) => `"${val}"`)
          .join(","),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      link.href = URL.createObjectURL(blob);
      link.setAttribute(
        "download",
        `Income_Verification_Report_${new Date()
          .toISOString()
          .split("T")[0]}.csv`
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success("Full report with totals exported successfully!");
    } catch (err) {
      console.error("Excel Export Error:", err);
      message.error("Export failed");
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setDistrictCode(null);
    setBlockCode(null);
    setGpCode(null);
    setCurrentPage(1);

    // Fetch data without filters
    fetchReport(1, pageSize);
  };


  return (
    <div className="report-container">
      <div className="report-card">
        <div className="report-header">
          <div>
            <h2 className="report-title">
              Income Verification Report
              {roleId === "8" && " (CEO-ZP)"}
              {roleId === "7" && " (BDPO)"}
              {roleId === "3" && " (Sarpanch)"}
            </h2>
            <p className="report-subtitle">
              Total Records: <strong>{totalRecords}</strong>
            </p>
          </div>

          <div className="report-actions">
            <Button
              onClick={handleExportPDF}
              icon={<FilePdfOutlined />}
              disabled={!data.length}
            >
              Export PDF
            </Button>

            <Button
              type="primary"
              onClick={handleExportExcel}
              icon={<FileExcelOutlined />}

              disabled={!data.length}
            >
              Export Excel
            </Button>

            <Button
              onClick={() => fetchReport(currentPage, pageSize)}
              icon={<ReloadOutlined />}

            >
              Refresh
            </Button>
          </div>
        </div>

        <Row gutter={12} style={{ marginBottom: 16 }}>
          {/* Show District filter for CEO (8) and Admin (1) */}
          {(roleId === "8" || roleId === "1" || roleId === "5" || roleId === "6") && (
            <Col xs={24} sm={6}>
              <Select
                allowClear
                placeholder="Select District"
                style={{ width: "100%" }}
                value={districtCode}
                loading={loadingDistricts}
                onChange={(val) => {
                  setDistrictCode(val);
                  setBlockCode(null);
                  setGpCode(null);
                }}
              >
                {districts.map((d) => (
                  <Select.Option key={d.district_code} value={d.district_code}>
                    {d.district}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          )}

          {/* Show Block filter for CEO (8), BDPO (7), and Admin (1) */}
          {(roleId === "8" || roleId === "7" || roleId === "1" || roleId === "5" || roleId === "6") && (
            <Col xs={24} sm={6}>
              <Select
                allowClear
                placeholder="Select Block"
                style={{ width: "100%" }}
                value={blockCode}
                loading={loadingBlocks}
                disabled={!districtCode && (roleId === "8" || roleId === "1" || roleId === "5" || roleId === "6")}
                onChange={(val) => {
                  setBlockCode(val);
                  setGpCode(null);
                }}
              >
                {blocks.map((b) => (
                  <Select.Option key={b.block_code} value={b.block_code}>
                    {b.block}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          )}

          {/* Show Gram Panchayat filter for all roles */}
          <Col xs={24} sm={6}>
            <Select
              allowClear
              placeholder="Select Gram Panchayat"
              style={{ width: "100%" }}
              value={gpCode}
              loading={loadingGPs}
              disabled={!blockCode}
              onChange={(val) => setGpCode(val)}
            >
              {gramPanchayats.map((gp) => (
                <Select.Option key={gp.gram_panchayat_code} value={gp.gram_panchayat_code}>
                  {gp.gram_panchayat}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={6}>
            <Button
              danger
              block
              onClick={handleClearFilters}
              disabled={!districtCode && !blockCode && !gpCode}
            >
              Clear All Filters
            </Button>
          </Col>
        </Row>

        {error && (
          <Alert
            type="error"
            message="Error loading data"
            description={error}
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        {loading ? (
          <div className="loading-container">
            <Spin size="large" tip="Loading report..." />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: currentPage,
              pageSize,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              total: totalRecords,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} records`,
            }}
            onChange={handleTableChange}
            bordered
            scroll={{ x: 1200, y: 420 }}
            size="small"
          />

        )}
        <div
          style={{
            backgroundColor: "#e6f7ff",
            border: "1px solid #91d5ff",
            borderRadius: "4px",
            padding: "8px 12px",
            marginTop: "8px",
            display: "flex",
            flexWrap: "wrap",
            gap: "14px",
            fontSize: "11px",
          }}
        >
          <span><strong>Totals :</strong></span>

          <span>Families: <strong>{overallTotals.total_families.toLocaleString()}</strong></span>
          <span>Members: <strong>{overallTotals.total_members.toLocaleString()}</strong></span>
          <span>Verified: <strong >{overallTotals.total_verified.toLocaleString()}</strong></span>
          <span>Not Verified: <strong >{overallTotals.total_not_verified.toLocaleString()}</strong></span>
          <span>Pending: <strong >{overallTotals.total_pending.toLocaleString()}</strong></span>
        </div>

      </div>
    </div>
  );
};

export default SchemeReport;
